import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cross-site cookie fallback: also carry the session token in Authorization header
const TOKEN_KEY = "zc_session_token";

// We use Bearer tokens (localStorage) for cross-site auth, so we do NOT need
// cookies. Setting withCredentials=true triggers Safari ITP to block responses
// that try to set cross-site cookies → causes spurious "Network Error".
axios.defaults.withCredentials = false;

// Attach bearer token from localStorage on every outbound request
axios.interceptors.request.use((config) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

const AuthContext = createContext({ user: null, loading: true, login: () => {}, logout: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // quickStart({email, name}) — simple email+name sign-in, no OAuth
  const quickStart = async ({ email, name }) => {
    const legacy_user_id = localStorage.getItem("zc_user_id") || null;
    const r = await axios.post(`${API}/auth/quick-start`, { email, name, legacy_user_id });
    const token = r.data?.session_token;
    if (token) {
      try { localStorage.setItem(TOKEN_KEY, token); } catch {}
    }
    if (legacy_user_id) localStorage.removeItem("zc_user_id");
    setUser({ user_id: r.data.user_id, email: r.data.email, name: r.data.name, picture: r.data.picture });
    return r.data;
  };

  // teamLogin({email, password}) — casino team-member sign-in
  const teamLogin = async ({ email, password }) => {
    const r = await axios.post(`${API}/team/login`, { email, password });
    const token = r.data?.session_token;
    if (token) {
      try { localStorage.setItem(TOKEN_KEY, token); } catch {}
    }
    // Re-fetch /auth/me so the user dict includes must_reset_password etc.
    await checkAuth();
    return r.data;
  };

  // teamSetPassword({email, old_password, new_password}) — team-member password change
  const teamSetPassword = async ({ email, old_password, new_password }) => {
    const r = await axios.post(`${API}/team/set-password`, { email, old_password, new_password });
    // Refresh user so must_reset_password flips to false
    await checkAuth();
    return r.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {}
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    // Note: we keep zc_last_email / zc_last_name so the next sign-in can autofill.
    // To fully forget the device, the user can uncheck "Remember me" on next sign-in.
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, quickStart, teamLogin, teamSetPassword, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// Kept as a no-op so old /?session_id= links don't crash — just bounces home
export function AuthCallback() {
  useEffect(() => { window.location.replace("/"); }, []);
  return null;
}

// Sign-in modal — email + name for owners; email + password for casino team members
// Sign-in modal — email + name for owners; email + password for casino team members
export function SignInModal({ open, onClose, message, initialMode }) {
  const { quickStart, teamLogin } = useAuth();
  const [mode, setMode] = useState(initialMode === "team" ? "team" : "owner"); // "owner" | "team"
  useEffect(() => { if (initialMode === "team") setMode("team"); }, [initialMode, open]);
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("zc_last_email") || ""; } catch { return ""; }
  });
  const [name, setName] = useState(() => {
    try { return localStorage.getItem("zc_last_name") || ""; } catch { return ""; }
  });
  // Re-sync email/name from localStorage when the modal opens. Fixes ?team_login=<email>
  // flow where App.js writes localStorage right before setting open=true — the
  // initializer above already ran with the OLD (empty) value, so we need a
  // second read once the modal is actually being shown.
  useEffect(() => {
    if (!open) return;
    try {
      const savedEmail = localStorage.getItem("zc_last_email") || "";
      const savedName = localStorage.getItem("zc_last_name") || "";
      if (savedEmail && !email) setEmail(savedEmail);
      if (savedName && !name) setName(savedName);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMode]);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!email.includes("@")) { setErr("Enter a valid email"); return; }
    if (mode === "owner" && !name.trim()) { setErr("Enter your name"); return; }
    if (mode === "team" && password.length < 4) { setErr("Enter your password"); return; }
    setBusy(true);
    try {
      if (mode === "team") {
        await teamLogin({ email: email.trim(), password });
      } else {
        await quickStart({ email: email.trim(), name: name.trim() });
      }
      if (remember) {
        try { localStorage.setItem("zc_last_email", email.trim()); if (mode === "owner") localStorage.setItem("zc_last_name", name.trim()); } catch {}
      } else {
        try { localStorage.removeItem("zc_last_email"); localStorage.removeItem("zc_last_name"); } catch {}
      }
      onClose?.();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const isTeam = mode === "team";
  return (
    <div className="fixed inset-0 z-[125] flex items-center justify-center p-4 bg-black/90 backdrop-blur-2xl" onClick={onClose}>
      <div data-testid="signin-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-surface border border-gold/40 rounded-sm overflow-hidden">
        <div className="h-32 bg-cover bg-center relative" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=900')" }}>
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-surface" />
        </div>
        <form onSubmit={submit} className="p-7">
          <div className="font-serif-accent text-gold text-sm">~ Member entrance</div>
          <div className="font-display uppercase text-2xl tracking-tight mt-1">
            {isTeam ? "Team Member Sign-in" : "Quick sign-in"}
          </div>
          <div className="text-muted text-sm leading-relaxed mt-3">
            {isTeam
              ? "For casino team members. Use the email and temporary password your account owner sent you."
              : (message || "Business owners and affiliates — no password needed. Just your name and email.")}
          </div>

          {!isTeam && (
            <>
              <label className="block mt-5 text-[11px] uppercase tracking-[0.2em] text-muted">Your name</label>
              <input
                data-testid="signin-name-input"
                autoFocus
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex Rivera"
                className="w-full mt-2 bg-bg border border-white/10 px-3.5 py-3 rounded-sm text-sm focus:border-gold/60 outline-none"
              />
            </>
          )}

          <label className="block mt-4 text-[11px] uppercase tracking-[0.2em] text-muted">Email</label>
          <input
            data-testid="signin-email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus={isTeam}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@vegas.com"
            className="w-full mt-2 bg-bg border border-white/10 px-3.5 py-3 rounded-sm text-sm focus:border-gold/60 outline-none"
          />

          {isTeam && (
            <>
              <label className="block mt-4 text-[11px] uppercase tracking-[0.2em] text-muted">Password</label>
              <input
                data-testid="team-signin-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Temporary or current password"
                className="w-full mt-2 bg-bg border border-white/10 px-3.5 py-3 rounded-sm text-sm focus:border-gold/60 outline-none"
              />
            </>
          )}

          {err && (
            <div data-testid="signin-error" className="mt-3 text-red-400 text-xs">{err}</div>
          )}

          <label className="flex items-center gap-2 mt-4 text-xs text-muted cursor-pointer">
            <input data-testid="signin-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-gold" />
            <span>Remember me on this device</span>
          </label>

          <button
            data-testid="signin-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full mt-5 font-display uppercase tracking-[0.2em] text-xs py-3.5 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50"
          >
            {busy ? "Holding the velvet rope…" : (isTeam ? "Enter Team Portal" : "Enter Zip City")}
          </button>

          <button
            type="button"
            data-testid="signin-toggle-mode"
            onClick={() => { setErr(""); setPassword(""); setMode(isTeam ? "owner" : "team"); }}
            className="w-full mt-3 text-xs text-gold/80 hover:text-gold underline underline-offset-4"
          >
            {isTeam ? "← Back to owner sign-in" : "Team Member? Log in here →"}
          </button>

          <button type="button" onClick={onClose} className="w-full mt-2 text-xs text-muted hover:text-white py-2">Maybe later</button>
        </form>
      </div>
    </div>
  );
}
